import { useEffect, useRef, useState, type ReactNode } from 'react';
import './ui.css';

/* ------------------------------------------------------------------ *
 * Registration plate
 *
 * A UK plate is the fastest thing for a fitter to recognise, so vehicles
 * are identified by a real plate rather than a line of text. Trailers
 * usually have no plate, so they get a riveted chassis tag instead.
 * ------------------------------------------------------------------ */

export function RegPlate({
  reg,
  fleetNo,
  size = 'md',
}: {
  reg?: string | null;
  fleetNo?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  if (reg) {
    return (
      <span className={`plate plate-${size}`} title={`Registration ${reg}`}>
        <span className="plate-band" aria-hidden="true">
          UK
        </span>
        <span className="plate-text">{formatReg(reg)}</span>
      </span>
    );
  }
  return (
    <span className={`tag tag-${size}`} title={fleetNo ? `Fleet number ${fleetNo}` : 'No identifier'}>
      {fleetNo || '—'}
    </span>
  );
}

/** Puts the conventional space into a current-style plate: AB12 CDE. */
export function formatReg(reg: string): string {
  const bare = reg.toUpperCase().replace(/\s+/g, '');
  if (/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(bare)) return `${bare.slice(0, 4)} ${bare.slice(4)}`;
  return reg.toUpperCase();
}

/* ------------------------------------------------------------------ *
 * Status
 *
 * StatusText is the default: a dot and tinted text, no box. It carries one
 * signal. StatusPill keeps a filled background and is reserved for the
 * states that must interrupt - overdue, safety, open defects.
 * ------------------------------------------------------------------ */

export type StatusState = 'overdue' | 'due' | 'soon' | 'ok' | 'unknown';

export function StatusText({
  state,
  children,
}: {
  state: StatusState | string;
  children: ReactNode;
}) {
  return <span className={`status status-${state}`}>{children}</span>;
}

export function StatusPill({
  state,
  children,
  size = 'md',
}: {
  state: StatusState | string;
  children: ReactNode;
  size?: 'sm' | 'md';
}) {
  return <span className={`pill pill-${state} pill-${size}`}>{children}</span>;
}

export function Badge({ tone = 'neutral', children }: { tone?: string; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

/* ------------------------------------------------------------------ *
 * Layout pieces
 * ------------------------------------------------------------------ */

/**
 * The one way a section is introduced. Title left, at most one action on
 * the right, always the same size and rhythm.
 */
export function SectionHeader({
  title,
  action,
  count,
}: {
  title: ReactNode;
  action?: ReactNode;
  count?: number | string;
}) {
  return (
    <div className="section-header">
      <h2 className="section-header-title">
        {title}
        {count !== undefined ? <span className="section-header-count num">{count}</span> : null}
      </h2>
      {action ? <div className="section-header-action">{action}</div> : null}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {error ? <span className="error-text">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/**
 * A settings toggle. It is a real checkbox underneath, so it is reachable by
 * keyboard and announced correctly; the visual switch is the label.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={`switchrow${disabled ? ' is-disabled' : ''}`}>
      <span className="grow">
        <span className="switchrow-label">{label}</span>
        {hint ? <span className="switchrow-hint">{hint}</span> : null}
      </span>
      <input
        type="checkbox"
        className="sr-only switchrow-input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch" aria-hidden="true">
        <span className="switch-knob" />
      </span>
    </label>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="spinner-wrap" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * A stand-in that keeps the screen's shape while data loads. Rows match the
 * list row layout; the hero variant matches the dashboard status block.
 */
export function Skeleton({
  rows = 4,
  variant = 'rows',
}: {
  rows?: number;
  variant?: 'rows' | 'hero' | 'detail';
}) {
  if (variant === 'hero') {
    return (
      <div className="skeleton-hero" role="status" aria-label="Loading">
        <span className="sk sk-line" style={{ width: '38%' }} />
        <span className="sk sk-display" style={{ width: '72%' }} />
        <span className="sk sk-line" style={{ width: '54%' }} />
      </div>
    );
  }
  if (variant === 'detail') {
    return (
      <div className="stack-4" role="status" aria-label="Loading">
        <div className="skeleton-hero">
          <span className="sk sk-plate" />
          <span className="sk sk-display" style={{ width: '60%' }} />
          <span className="sk sk-line" style={{ width: '40%' }} />
        </div>
        <div className="panel">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="skeleton-row">
              <span className="sk sk-line" style={{ width: '48%' }} />
              <span className="sk sk-line" style={{ width: '22%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="panel" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-row">
          <span className="sk sk-plate" />
          <span className="skeleton-lines">
            <span className="sk sk-line" style={{ width: `${62 - (i % 3) * 9}%` }} />
            <span className="sk sk-line sk-thin" style={{ width: `${44 + (i % 2) * 12}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h3 className="heading">{title}</h3>
      {body ? <p className="small muted">{body}</p> : null}
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * List controls
 *
 * One toolbar for every list screen: quick filter chips inline, and
 * everything else - sort order and the narrower filters - behind a single
 * button, because a phone has no room for a row of dropdowns.
 * ------------------------------------------------------------------ */

export interface SortOption<T extends string = string> {
  key: T;
  label: string;
  hint?: string;
}

export function ListControls<F extends string, S extends string>({
  chips,
  chip,
  onChip,
  sortOptions,
  sort,
  onSort,
  filters,
  activeFilterCount = 0,
  onClearFilters,
}: {
  chips?: { key: F; label: string }[];
  chip?: F;
  onChip?: (key: F) => void;
  sortOptions: SortOption<S>[];
  sort: S;
  onSort: (key: S) => void;
  /** Extra filter controls, rendered inside the sheet. */
  filters?: ReactNode;
  activeFilterCount?: number;
  onClearFilters?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const current = sortOptions.find((o) => o.key === sort);

  return (
    <>
      <div className="controls">
        {chips && chips.length > 0 ? (
          <div className="chips" role="tablist" aria-label="Filter">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={chip === c.key}
                className={`chip${chip === c.key ? ' is-on' : ''}`}
                onClick={() => onChip?.(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="grow" />
        )}

        <button
          type="button"
          className={`chip chip-sort${activeFilterCount > 0 ? ' is-on' : ''}`}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
        >
          <Icon name="sort" size={15} />
          <span className="chip-sort-label">{current?.label ?? 'Sort'}</span>
          {activeFilterCount > 0 ? <span className="chip-count">{activeFilterCount}</span> : null}
        </button>
      </div>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Sort and filter"
        footer={
          <>
            {onClearFilters ? (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  onClearFilters();
                  setOpen(false);
                }}
                disabled={activeFilterCount === 0}
              >
                Clear
              </button>
            ) : null}
            <button className="btn btn-primary" onClick={() => setOpen(false)}>
              Done
            </button>
          </>
        }
      >
        <div className="stack-5">
          <div className="stack-2">
            <span className="label">Sort by</span>
            <div className="optionlist" role="radiogroup" aria-label="Sort by">
              {sortOptions.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  role="radio"
                  aria-checked={sort === o.key}
                  className={`option${sort === o.key ? ' is-on' : ''}`}
                  onClick={() => onSort(o.key)}
                >
                  <span className="grow">
                    <span className="option-label">{o.label}</span>
                    {o.hint ? <span className="option-hint">{o.hint}</span> : null}
                  </span>
                  {sort === o.key ? <Icon name="check" size={17} /> : null}
                </button>
              ))}
            </div>
          </div>

          {filters ? <div className="stack-4">{filters}</div> : null}
        </div>
      </Sheet>
    </>
  );
}

/**
 * Remembers a list's sort and filter choices per screen, so returning to a
 * list does not throw away how the user had it arranged.
 */
export function useListPrefs<T extends Record<string, unknown>>(key: string, defaults: T) {
  const [prefs, setPrefs] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`fc.list.${key}`);
      return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
    } catch {
      return defaults;
    }
  });

  const update = (patch: Partial<T>) =>
    setPrefs((current) => {
      const next = { ...current, ...patch };
      try {
        localStorage.setItem(`fc.list.${key}`, JSON.stringify(next));
      } catch {
        /* private browsing - the choice just will not persist */
      }
      return next;
    });

  const reset = () => {
    try {
      localStorage.removeItem(`fc.list.${key}`);
    } catch {
      /* ignore */
    }
    setPrefs(defaults);
  };

  return [prefs, update, reset] as const;
}

/* ------------------------------------------------------------------ *
 * Bottom sheet
 *
 * Modal dialogs sit at the bottom on a phone so they are reachable with a
 * thumb. Uses <dialog> for the focus trap and Escape handling.
 * ------------------------------------------------------------------ */

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className="sheet"
      onClick={(e) => {
        // Clicking the backdrop (the dialog element itself) dismisses.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="sheet-inner">
        <span className="sheet-grip" aria-hidden="true" />
        <header className="sheet-head">
          <h2>{title}</h2>
          <button type="button" className="btn btn-quiet sheet-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>
        <div className="sheet-body">{children}</div>
        {footer ? <footer className="sheet-foot">{footer}</footer> : null}
      </div>
    </dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Progress
 * ------------------------------------------------------------------ */

export function ProgressBar({ value, total, tone = 'brand' }: { value: number; total: number; tone?: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div
      className={`progress progress-${tone}${pct >= 100 ? ' is-complete' : ''}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={`${value} of ${total} complete`}
    >
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Icons
 *
 * Hand-drawn line icons on a 24-unit grid at a 1.75 stroke. The five tab
 * icons also have filled forms for the active state; internal detail is
 * cut back in with the surface colour so the silhouette stays legible.
 * ------------------------------------------------------------------ */

const ICONS: Record<string, ReactNode> = {
  gauge: (
    <>
      <path d="M12 13.5 16.5 9" />
      <path d="M3.5 18a9 9 0 1 1 17 0" />
      <circle cx="12" cy="13.5" r="1.4" />
    </>
  ),
  truck: (
    <>
      <path d="M2.5 16.5V6.5h11v10" />
      <path d="M13.5 9.5h3.6l2.9 3.2v3.8h-6.5" />
      <circle cx="7" cy="17.5" r="1.9" />
      <circle cx="16.5" cy="17.5" r="1.9" />
    </>
  ),
  clipboard: (
    <>
      <path d="M9 4.5H7.5A1.5 1.5 0 0 0 6 6v13a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 18 19V6a1.5 1.5 0 0 0-1.5-1.5H15" />
      <rect x="9" y="3" width="6" height="3" rx="1" />
      <path d="M9 11h6M9 14.5h4" />
    </>
  ),
  spanner: (
    <>
      <path d="M15.8 4.4a4.6 4.6 0 0 0-5.6 5.9L4.6 15.9a1.8 1.8 0 0 0 2.5 2.5l5.6-5.6a4.6 4.6 0 0 0 5.9-5.6l-2.6 2.6-2.4-.6-.6-2.4z" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
  back: (
    <>
      <path d="M15 5l-7 7 7 7" />
    </>
  ),
  chevron: (
    <>
      <path d="M9 5l7 7-7 7" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12M18 6 6 18" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5 20 20" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4.5 21 19.5H3z" />
      <path d="M12 10v4M12 16.8v.2" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
    </>
  ),
  bell: (
    <>
      <path d="M18 9.5a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5z" />
      <path d="M10.2 19.5a2.1 2.1 0 0 0 3.6 0" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.8 7 8.2 6 8.2-6" />
    </>
  ),
  check: (
    <>
      <path d="M5 12.5 10 17.5 19 7" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M4.5 20c0-3.6 3.4-5.8 7.5-5.8s7.5 2.2 7.5 5.8" />
    </>
  ),
  invoice: (
    <>
      <path d="M6 3.5h12v17l-3-1.8-3 1.8-3-1.8-3 1.8z" />
      <path d="M9.5 8.5h5M9.5 12.5h5" />
    </>
  ),
  building: (
    <>
      <path d="M4 20.5V5.5l8-3 8 3v15" />
      <path d="M9 20.5v-5h6v5" />
      <path d="M8.5 9.5h1.5M14 9.5h1.5M8.5 12.5h1.5M14 12.5h1.5" />
    </>
  ),
  logout: (
    <>
      <path d="M14.5 8V5.5A1.5 1.5 0 0 0 13 4H6a1.5 1.5 0 0 0-1.5 1.5v13A1.5 1.5 0 0 0 6 20h7a1.5 1.5 0 0 0 1.5-1.5V16" />
      <path d="M10 12h10m0 0-3-3m3 3-3 3" />
    </>
  ),
  sort: (
    <>
      <path d="M4 7h16M7 12h10M10 17h4" />
    </>
  ),
  share: (
    <>
      <path d="M12 3.5v11" />
      <path d="M8.5 7 12 3.5 15.5 7" />
      <path d="M6 11.5H5a1.5 1.5 0 0 0-1.5 1.5v6A1.5 1.5 0 0 0 5 20.5h14a1.5 1.5 0 0 0 1.5-1.5v-6a1.5 1.5 0 0 0-1.5-1.5h-1" />
    </>
  ),
};

/** Filled forms for the tab bar's active state. Detail is cut in with the surface colour. */
const FILLED: Record<string, ReactNode> = {
  gauge: (
    <>
      <path d="M3 18.5a9.5 9.5 0 1 1 18 0z" fill="currentColor" stroke="none" />
      <path d="M12 14 16.8 9.2" stroke="var(--surface-0)" strokeWidth="2" />
      <circle cx="12" cy="14" r="1.6" fill="var(--surface-0)" stroke="none" />
    </>
  ),
  truck: (
    <>
      <path d="M2.5 6.5h11v10h-11z" fill="currentColor" stroke="none" />
      <path d="M13.5 9.5h3.6l2.9 3.2v3.8h-6.5z" fill="currentColor" stroke="none" />
      <circle cx="7" cy="17.5" r="2.4" fill="currentColor" stroke="var(--surface-0)" strokeWidth="1.5" />
      <circle cx="16.5" cy="17.5" r="2.4" fill="currentColor" stroke="var(--surface-0)" strokeWidth="1.5" />
    </>
  ),
  clipboard: (
    <>
      <rect x="6" y="4.5" width="12" height="16" rx="1.6" fill="currentColor" stroke="none" />
      <rect x="9" y="3" width="6" height="3" rx="1" fill="currentColor" stroke="var(--surface-0)" strokeWidth="1.2" />
      <path d="M9 11h6M9 14.5h4" stroke="var(--surface-0)" strokeWidth="1.6" />
    </>
  ),
  spanner: (
    <>
      <path
        d="M15.8 4.4a4.6 4.6 0 0 0-5.6 5.9L4.6 15.9a1.8 1.8 0 0 0 2.5 2.5l5.6-5.6a4.6 4.6 0 0 0 5.9-5.6l-2.6 2.6-2.4-.6-.6-2.4z"
        fill="currentColor"
        stroke="none"
      />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="2.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="2.1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="2.1" fill="currentColor" stroke="none" />
    </>
  ),
};

export function Icon({
  name,
  size = 22,
  filled = false,
}: {
  name: keyof typeof ICONS | string;
  size?: number;
  filled?: boolean;
}) {
  const body = filled && FILLED[name] ? FILLED[name] : ICONS[name];
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {body ?? null}
    </svg>
  );
}
